import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Hand } from "lucide-react";
import EventCard from "./EventCard";

export default function EventsCarousel({ events = [] }) {
  const ref = useRef(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [progress, setProgress] = useState(0); // 0..1 scroll position on mobile

  const scrollBy = (dir) => {
    if (!ref.current) return;
    const cardWidth = ref.current.firstChild?.firstChild?.offsetWidth || 300;
    ref.current.scrollBy({ left: dir * (cardWidth + 24), behavior: "smooth" });
  };

  // Hide swipe hint after the first user-initiated touch/scroll, or after 8 seconds
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const hide = () => setShowSwipeHint(false);
    el.addEventListener("touchstart", hide, { passive: true, once: true });
    el.addEventListener("pointerdown", hide, { passive: true, once: true });
    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      setProgress(max > 0 ? Math.min(1, el.scrollLeft / max) : 0);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const timer = setTimeout(hide, 8000);
    return () => {
      el.removeEventListener("touchstart", hide);
      el.removeEventListener("pointerdown", hide);
      el.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, []);

  const hasMultiple = events.length > 1;

  return (
    <div className="relative" data-testid="events-carousel">
      {/* MOBILE: scroll progress bar only (text cue lives on the page above) */}
      {hasMultiple && (
        <div className="md:hidden mb-3 h-[2px] bg-white/10 rounded-full overflow-hidden" data-testid="carousel-progress">
          <div
            className="h-full bg-gradient-to-r from-[#D4AF7A] to-[#B76E79] transition-[width] duration-200"
            style={{ width: `${Math.max(8, progress * 100)}%` }}
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => scrollBy(-1)}
        className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-black/70 border border-white/15 text-white hover:bg-[#B76E79]/80 hover:border-[#B76E79] transition shadow-xl backdrop-blur"
        aria-label="Previous"
        data-testid="carousel-prev"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-black/70 border border-white/15 text-white hover:bg-[#B76E79]/80 hover:border-[#B76E79] transition shadow-xl backdrop-blur"
        aria-label="Next"
        data-testid="carousel-next"
      >
        <ChevronRight size={20} />
      </button>

      <div className="relative">
        <div
          ref={ref}
          className="overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0"
        >
          <div className="flex gap-5 md:gap-6 pb-2">
            {events.map((e, i) => (
              <div
                key={e.id}
                className="snap-start shrink-0 w-[78vw] sm:w-[340px] md:w-[300px] lg:w-[320px]"
              >
                <EventCard event={e} index={i} />
              </div>
            ))}
          </div>
        </div>

        {/* MOBILE: animated arrow on the right edge — pulses to signal more cards */}
        {hasMultiple && (
          <div
            className={`md:hidden absolute top-1/2 -translate-y-1/2 right-1 pointer-events-none transition-opacity duration-500 ${
              showSwipeHint && progress < 0.15 ? "opacity-100" : "opacity-0"
            }`}
            data-testid="swipe-edge-arrow"
            aria-hidden
          >
            <div className="w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-[#B76E79]/50 flex items-center justify-center shadow-[0_0_24px_rgba(232,180,184,0.5)] animate-swipe-hint">
              <ChevronRight size={18} className="text-[#E8B4B8]" strokeWidth={2.5} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
